import BransjePicker, { PickedBransje } from 'components/BransjePicker';
import CountryPicker from 'components/CountryPicker';
import SegmentPicker from 'components/SegmentPicker';
import useSegmenterPerBransje from 'components/SegmentPicker/useSegmenterPerBransje';
import { getSegmenterForBransje } from 'components/SegmentPicker/utils';
import EnhetTypeMultiPicker from 'components/EnhetTypeMultiPicker';
import { PickedType } from 'components/EnhetTypeMultiPicker/types';
import { addMinutes, format } from 'date-fns';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { Col, Form, Row, Button } from 'react-bootstrap';
import { useMsal } from '@azure/msal-react';
import EnhetApi from 'services/KjederegisteretAdminApi/EnhetApi';
import { UpdateEnhetCommand } from 'services/KjederegisteretAdminApi/EnhetApi/Commands';
import {
  EnhetTyperViewModel,
  EnhetViewModel,
  KontonrOgFakturaAdresseSjekkResponse,
} from 'services/KjederegisteretAdminApi/EnhetApi/types';
import useForm from 'utils/hooks/useForm';
import useIsMountedRef from 'utils/hooks/useIsMountedRef';
import useToaster from 'utils/hooks/useToaster';
import { EmailValidator } from 'utils/EmailValidator';
import HttpErrorHelper from 'utils/HttpErrorHelper';
import RemoveSpaceAndDot from 'utils/RemoveSpaceAndDot';
import Style from './index.module.css';
import { validateKontonummerMod11 } from 'pages/EnhetSearch/utils';
import ElmaApi from 'services/KjederegisteretAdminApi/ElmaApi';
import EmailsApi from 'services/AdminportalenApi/EmailsApi';
import { NorwegianDatepicker } from 'components/NorwegianDatepicker';
import OrgnrInput, { BrRegInfo } from 'components/OrgnrInput';
import { IcebergButton } from '@tradesolution/iceberg-ui-react';
import EnhetKildeApi from 'services/KjederegisteretAdminApi/EnhetKildeApi';
import { KildeDetaljerViewModel } from 'services/KjederegisteretAdminApi/EnhetKildeApi/types';
import { EnhetLookupContext } from 'services/KjederegisteretAdminApi/EnhetMatchLookupApi/types';
import FieldSourcesPopover from './FieldSourcesPopover';
import KildeSuggestionInline from './KildeSuggestionInline';

export interface BasisRef {
  save: () => void;
  isSaveDisabled: () => boolean;
  isSaving: () => boolean;
}

interface Props {
  enhet: EnhetViewModel;
  onUpdated: () => void;
  linkedForm: { tab: string; field: string };
  kontonrOgFakturaAdresseSjekkResponse?: KontonrOgFakturaAdresseSjekkResponse;
}

const Basis = forwardRef<BasisRef, Props>((props, ref) => {
  const toaster = useToaster();
  const { accounts } = useMsal();
  const username = accounts[0] && accounts[0].name;
  const currentUser = username || 'ukjent';

  const [isSaveLoading, setIsSaveLoading] = useState(false);
  // Daglig leder from Brreg's rolle-data for the juridisk hovedenhet (organisasjonsnummer) -
  // fetched by OrgnrInput itself (it already needs this for its own popover), just surfaced here too.
  const [brregDagligLeder, setBrregDagligLeder] = useState<string | undefined>(undefined);
  // Same reuse pattern for NACE-kode - shown next to "Bransje" for reference. Underenhet's own
  // kode wins if set, since that's more specific than the juridisk hovedenhet's.
  type Naeringskode = { kode: string; beskrivelse: string };
  const [naeringskodeOrgnr, setNaeringskodeOrgnr] = useState<Naeringskode | undefined>(undefined);
  const [naeringskodeBedrift, setNaeringskodeBedrift] = useState<Naeringskode | undefined>(
    undefined,
  );
  const brregNaeringskode = naeringskodeBedrift ?? naeringskodeOrgnr;
  const [selectedOrgnummer, setSelectedJuridisk] = useState<BrRegInfo | undefined>(
    props.enhet.firma.juridisknavn && props.enhet.firma.organisasjonsnr
      ? { name: props.enhet.firma.juridisknavn, orgNr: props.enhet.firma.organisasjonsnr }
      : undefined,
  );

  const [selectedBedriftsnummer, setSelectedBedriftsnummer] = useState<BrRegInfo | undefined>(
    props.enhet.firma.juridisknavnBedrift && props.enhet.firma.bedriftsnr
      ? { name: props.enhet.firma.juridisknavnBedrift, orgNr: props.enhet.firma.bedriftsnr }
      : undefined,
  );

  const [isBedriftsnummerInElma, setIsBedriftsnummerInElma] = useState<boolean | undefined>(
    undefined,
  );
  // Either the juridisk hovedenhet's organisasjonsnummer or the underenhet's bedriftsnummer can
  // be the one actually registered for e-faktura - checking only one side left this ambiguous,
  // so both get checked and shown so it's clear which number to actually use.
  const [isOrgnummerInElma, setIsOrgnummerInElma] = useState<boolean | undefined>(undefined);

  const [postFrom, setPostFrom] = useState<string>(
    format(new Date(props.enhet.firma.postFraOgMedDato), 'dd.MM.yyyy'),
  );

  const [pickedBransjeID, setPickedBransjeID] = useState<number | undefined>(
    props.enhet.bransje?.id,
  );
  const segmenterPerBransje = useSegmenterPerBransje();
  const segmentErRelevant = getSegmenterForBransje(segmenterPerBransje, pickedBransjeID).length > 0;
  const [pickedEnhetTypeKoder, setPickedEnhetTypeKoder] = useState<string[]>(
    props.enhet.enhetTyper?.map((x: EnhetTyperViewModel) => x.kode),
  );
  const [pickerResetIndicator, setPickerResetIndicator] = useState<string>();
  const isMountedRef = useIsMountedRef();

  // Hjemmeside link state - only updates on blur to avoid flickering while typing
  const [hjemmesideLink, setHjemmesideLink] = useState<string | undefined>(() => {
    const val = props.enhet.hjemmeside;
    if (!val || !val.trim()) return undefined;
    return val.match(/^https?:\/\//i) ? val : `https://${val}`;
  });

  const updateHjemmesideLink = (value: string | undefined) => {
    if (!value || !value.trim()) {
      setHjemmesideLink(undefined);
    } else {
      setHjemmesideLink(value.match(/^https?:\/\//i) ? value : `https://${value}`);
    }
  };

  const [loadingFørsteMuligeOppstartDato, setLoadingFørsteMuligeOppstartDato] = useState(false);
  const [førsteMuligeOppstartDato, setFørsteMuligeOppstartDato] = useState<Date | undefined>();

  const loadFørsteMuligeOppstartDato = async (enhetId: number) => {
    setLoadingFørsteMuligeOppstartDato(true);
    const dato = await EnhetApi.førsteMuligeOppstartDato(enhetId);
    setFørsteMuligeOppstartDato(dato);
    setLoadingFørsteMuligeOppstartDato(false);
  };

  const defaultValues: UpdateEnhetCommand = {
    enhetnavn: props.enhet.enhetnavn,
    epost: props.enhet.firma.epost?.trim(),
    fakturaepost: props.enhet.firma.fakturaEpost?.trim(),
    erOffentlig: props.enhet.firma.erOffentlig,
    erSesongDrift: props.enhet.firma.erSesongdrift,
    reservertMotPost: props.enhet.firma.reservertMotPost,
    kanMottaEfaktura: props.enhet.firma.kanMottaEfaktura,
    id: props.enhet.id,
    juridisknavn: props.enhet.firma.juridisknavn,
    kontonr: props.enhet.firma.kontonr,
    landKode: props.enhet.firma.landkode,
    notat: props.enhet.notat,
    organisasjonsnummer: props.enhet.firma.organisasjonsnr,
    juridisknavnBedrift: props.enhet.firma.juridisknavnBedrift,
    bedriftsnummer: props.enhet.firma.bedriftsnr,
    telefon: props.enhet.firma.telefon,
    omsetningAar: props.enhet.omsetning?.year,
    omsetningMillioner: props.enhet.firma.omsetningMillioner,
    oppstartsdato: props.enhet.firma.oppstartsdato,
    postFraOgMedDato: props.enhet.firma.postFraOgMedDato,
    salgsArealKvadratmeter: props.enhet.firma.salgsArealKvadratmeter,
    bransjeId: props.enhet.bransje?.id,
    segment: props.enhet.segment,
    enhetTypeKoder: props.enhet.enhetTyper?.map((x: EnhetTyperViewModel) => x.kode),
    hovedEnhetTypeKode: props.enhet.enhetTyper?.find((x: EnhetTyperViewModel) => x.erHovedtype)
      ?.kode,
    hjemmeside: props.enhet.hjemmeside,
    dagligLeder: props.enhet.dagligLeder,
  };

  const validate = () => {
    const errors: any = {};
    if (!formData.values?.enhetnavn) errors.enhetnavn = 'Enhetnavn mangler!';
    if (formData.values?.telefon?.length > 8)
      errors.telefon = 'Telefonnummer må være 8 tegn eller mindre';
    if (formData.values?.telefon && !/^\d+$/.test(formData.values?.telefon))
      errors.telefon = 'Telefonnummer kan kun bestå av tall';
    if (formData.values?.epost && !EmailValidator.isValidPattern(formData.values?.epost))
      errors.epost = 'Epost må være gyldig';
    if (
      formData.values?.fakturaepost &&
      !EmailValidator.isValidPattern(formData.values?.fakturaepost)
    )
      errors.fakturaepost = 'Epost må være gyldig';
    if (formData.values?.kontonr && !validateKontonummerMod11(formData.values?.kontonr))
      errors.kontonr = 'Kontonummer må være gyldig';
    if (
      selectedOrgnummer &&
      selectedBedriftsnummer &&
      selectedOrgnummer.orgNr.trim() === selectedBedriftsnummer.orgNr.trim()
    )
      errors.bedriftsnr = 'Organisasjonsnummer og bedriftsnummer kan ikke være like';
    if (selectedOrgnummer?.isBedriftsNr)
      errors.orgnr = errors.orgnr =
        'Dette er et bedriftsnummer og skal ikke brukes som organisasjonsnummer';
    return errors;
  };

  const onSubmit = async (values: UpdateEnhetCommand) => {
    if (values.oppstartsdato) {
      values.oppstartsdato = addMinutes(
        values.oppstartsdato,
        -values.oppstartsdato.getTimezoneOffset(),
      );
    }

    if (values.postFraOgMedDato) {
      values.postFraOgMedDato = addMinutes(
        values.postFraOgMedDato,
        -values.postFraOgMedDato.getTimezoneOffset(),
      );
    }

    setIsSaveLoading(true);
    try {
      await EnhetApi.updateEnhet(values);
      toaster.success('Suksess', 'Enhet oppdatert!');
      setIsSaveLoading(false);
      formData.resetForm(values);
      props.onUpdated();
    } catch (err) {
      toaster.error('Noe gikk galt.', HttpErrorHelper.formatError(err));
      setIsSaveLoading(false);
    }
  };

  const updateFormBrregHandler = (type: 'orgnr' | 'bedriftsnr', name: string, orgNr: string) => {
    const copy = { ...formData.values };
    if (type === 'orgnr') {
      copy.organisasjonsnummer = orgNr;
      copy.juridisknavn = name ? name.toUpperCase() : null;
    } else if (type === 'bedriftsnr') {
      copy.bedriftsnummer = orgNr;
      copy.juridisknavnBedrift = name ? name.toUpperCase() : null;
    }
    formData.updateValues(copy);
  };

  const insertNameAndDate = () => {
    const dateStr = format(new Date(), 'dd.MM.yyyy');
    const prefix = `${currentUser} ${dateStr}: `;
    const newNotat = formData.values.notat ? `${prefix}\n${formData.values.notat}` : prefix;
    formData.handleChange('notat', newNotat);
  };

  // remove spaces and dots from number strings (kontonr, tlfnr)
  const handleInputChangeClean = (field: string, val: string) => {
    const result = RemoveSpaceAndDot(val);
    formData.handleChange(field, result);
  };

  const handleInputChange = (field: string, val: string) => {
    formData.handleChange(field, val);
  };

  const formData = useForm<UpdateEnhetCommand, any>(defaultValues, validate, onSubmit);
  const [isEnhetnavnFormatCorrect, setIsEnhetnavnFormatCorrect] = useState(true);

  useImperativeHandle(ref, () => ({
    save: () => {
      formData.handleSubmit({ preventDefault: () => {} } as React.FormEvent<HTMLFormElement>);
    },
    isSaveDisabled: () => formData.submitDisabled,
    isSaving: () => isSaveLoading,
  }));

  const checkEnhetnavnFormat = (enhetnavn) => {
    // This regex checks if 'AVD' or 'avd' is present but not followed by a period and numbers
    const incorrectFormatRegex = /\bavd\b(?!\.\d+)/i;

    // This regex checks if 'AVD' or 'avd' is followed by a period and numbers
    const correctFormatRegex = /\bavd\.\d+/i;

    // If 'AVD' or 'avd' is present and not followed by the correct format, return false
    if (incorrectFormatRegex.test(enhetnavn) && !correctFormatRegex.test(enhetnavn)) {
      return false;
    }
    // If 'AVD' or 'avd' is not present or followed by the correct format, return true
    return true;
  };

  useEffect(() => {
    // If enhetnavn is not empty, check its format
    if (formData.values.enhetnavn) {
      setIsEnhetnavnFormatCorrect(checkEnhetnavnFormat(formData.values.enhetnavn));
    } else {
      // If enhetnavn is empty, we consider the format correct by default (no message shown)
      setIsEnhetnavnFormatCorrect(true);
    }
  }, [formData.values.enhetnavn]);

  const checkPostDate = () => {
    const dateOffset = 24 * 60 * 60 * 1000 * 14; //14 days in ms
    const oppstart = new Date(formData.values.oppstartsdato); // base on oppstart
    const postDate = new Date(oppstart.getTime() - dateOffset); // subtract offsett ms from oppstart ms

    if (formData.touched.oppstartsdato) {
      setPostFrom(format(postDate, 'dd.MM.yyyy'));
    }
  };

  //validering av e-poster
  const [isEmailValid, setIsEmailValid] = useState<boolean>(true);
  const verifyEmail = async (email: string) => {
    const verifyResult = await EmailsApi.verify(email);
    setIsEmailValid(verifyResult.canBeUsed);
  };

  const [isFakturaEpostValid, setIsFakturaEpostValid] = useState<boolean>(true);
  const verifyFaktura = async (email: string) => {
    const verifyResult = await EmailsApi.verify(email);
    setIsFakturaEpostValid(verifyResult.canBeUsed);
  };

  // bransje
  const handlePickedBransjeChanged = useCallback((val: PickedBransje[]) => {
    if (val?.length > 0) {
      setPickedBransjeID(val[0].id);
    } else {
      setPickedBransjeID(undefined);
    }
  }, []);

  const handleOppstartDatoChange = (date: Date) => {
    const copy = { ...formData.values };
    copy.oppstartsdato = date || null;
    formData.updateValues(copy);
  };

  const handleSelectedTyperChanged = (vals: PickedType[]) => {
    setPickedEnhetTypeKoder(vals.map((x) => x.kode) || []);
  };

  useEffect(() => {
    if (selectedBedriftsnummer) {
      (async () => {
        const result = await ElmaApi.isRegistered(selectedBedriftsnummer.orgNr);
        setIsBedriftsnummerInElma(result);
      })();
    } else {
      setIsBedriftsnummerInElma(undefined);
    }
  }, [selectedBedriftsnummer?.orgNr]);

  useEffect(() => {
    if (selectedOrgnummer) {
      (async () => {
        const result = await ElmaApi.isRegistered(selectedOrgnummer.orgNr);
        setIsOrgnummerInElma(result);
      })();
    } else {
      setIsOrgnummerInElma(undefined);
    }
  }, [selectedOrgnummer?.orgNr]);

  useEffect(() => {
    if (isMountedRef.current) {
      formData.handleChange('bransjeId', pickedBransjeID);
    }
  }, [pickedBransjeID]);

  useEffect(() => {
    if (defaultValues) {
      formData.resetForm(defaultValues);
      if (props.enhet.firma.juridisknavn && props.enhet.firma.organisasjonsnr) {
        if (
          selectedOrgnummer?.name !== props.enhet.firma.juridisknavn ||
          selectedOrgnummer?.orgNr !== props.enhet.firma.organisasjonsnr
        ) {
          setSelectedJuridisk({
            name: props.enhet.firma.juridisknavn,
            orgNr: props.enhet.firma.organisasjonsnr,
          });
        }
      } else {
        setSelectedJuridisk(undefined);
      }
      if (
        props.enhet.firma.juridisknavnBedrift &&
        props.enhet.firma.bedriftsnr &&
        selectedBedriftsnummer
      ) {
        if (
          selectedBedriftsnummer[0]?.name !== props.enhet.firma.juridisknavnBedrift &&
          selectedBedriftsnummer[0]?.orgNr !== props.enhet.firma.bedriftsnr
        ) {
          setSelectedBedriftsnummer({
            name: props.enhet.firma.juridisknavnBedrift,
            orgNr: props.enhet.firma.bedriftsnr,
          });
        }
      } else {
        setSelectedBedriftsnummer(undefined);
      }
    }
    loadFørsteMuligeOppstartDato(props.enhet.id);
    setPickedBransjeID(props.enhet.bransje?.id);
  }, [props.enhet]);

  useEffect(() => {
    if (props.linkedForm.field) {
      const el = document.getElementById(props.linkedForm.field);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        if (typeof el.focus === 'function' && el.tagName !== 'DIV') {
          el.focus({ preventScroll: true });
        }
      }
    }
  }, [props.linkedForm]);

  useEffect(() => {
    if (
      props.enhet.firma.reservertMotPost &&
      !props.enhet.firma.oppstartsdato &&
      formData.values.oppstartsdato &&
      formData.touched.oppstartsdato
    ) {
      formData.handleChange('reservertMotPost', false);
    }
    checkPostDate();
  }, [formData.values.oppstartsdato]);

  useEffect(() => {
    if (
      formData.values.juridisknavn === defaultValues.juridisknavn &&
      formData.values.organisasjonsnummer === defaultValues.organisasjonsnummer
    ) {
      formData.resetValues(['juridisknavn', 'organisasjonsnummer']);
    }
  }, [formData.values.juridisknavn, formData.values.organisasjonsnummer]);

  useEffect(() => {
    if (isMountedRef.current) {
      formData.handleChange('enhetTypeKoder', pickedEnhetTypeKoder);
    }
  }, [pickedEnhetTypeKoder]);

  useEffect(() => {
    if (formData && formData.values?.enhetTypeKoder?.length === 1) {
      formData.handleChange('hovedEnhetTypeKode', formData.values.enhetTypeKoder[0]);
    }
  }, [formData.values.enhetTypeKoder]);

  useEffect(() => {
    setPickerResetIndicator(props.enhet.lopenr);
  }, [props.enhet.lopenr]);

  const handleOrgnrChanged = (value: BrRegInfo | undefined): void => {
    if (value) {
      updateFormBrregHandler('orgnr', value.name, value.orgNr);
      setSelectedJuridisk(value);
    } else {
      // Clear selection
      setSelectedJuridisk(undefined);
      formData.updateValues({
        ...formData.values,
        organisasjonsnummer: null,
        juridisknavn: null,
      });
    }
  };

  const handleBedriftsnummerChanged = (value: BrRegInfo | undefined): void => {
    if (value) {
      updateFormBrregHandler('bedriftsnr', value.name, value.orgNr);
      setSelectedBedriftsnummer(value);
    } else {
      setSelectedBedriftsnummer(undefined);
      formData.updateValues({
        ...formData.values,
        bedriftsnummer: null,
        juridisknavnBedrift: null,
      });
    }
  };

  // Live per-field values from every linked kilde, shown via FieldSourcesPopover next to
  // individual form fields so the user can compare/pick a value from a specific source - fetched
  // once per Enhet rather than per-field, since it's the same underlying data for every popover.
  const [kildeDetaljer, setKildeDetaljer] = useState<KildeDetaljerViewModel[]>([]);
  // Each KildeSuggestionInline reports back whether it actually found anything, so the
  // "Kildeforslag:" label only shows up once there's something to show under it.
  const [brregHasSuggestion, setBrregHasSuggestion] = useState(false);
  const [googlePlacesHasSuggestion, setGooglePlacesHasSuggestion] = useState(false);
  const [facebookHasSuggestion, setFacebookHasSuggestion] = useState(false);

  useEffect(() => {
    let cancelled = false;
    EnhetKildeApi.getKildeDetaljer(props.enhet.id)
      .then((result) => {
        if (!cancelled) setKildeDetaljer(result);
      })
      .catch(() => {
        // Best-effort - popovers simply won't show source values if this fails
      });

    return () => {
      cancelled = true;
    };
    // props.enhet (not just .id) so a re-fetch after linking/unlinking a kilde (which refreshes
    // the Enhet via onUpdated, producing a new props.enhet reference) is actually picked up here -
    // .id alone never changes on a same-Enhet refresh.
  }, [props.enhet]);

  // Same trigger conditions the old proactive banners used, now gating a lightweight inline
  // suggestion instead - and additionally suppressed once that source is already linked (kildeDetaljer),
  // since re-searching a connected source here would just be noise.
  const isBrregUnderenhetLinked = kildeDetaljer.some((k) => k.kilde === 'brreg-underenhet');
  const showBrregSuggestion =
    !isBrregUnderenhetLinked &&
    !props.enhet.firma?.bedriftsnr &&
    !!props.enhet.firma?.organisasjonsnr;
  const brregLookupContext: EnhetLookupContext = {
    organisasjonsnummer: props.enhet.firma?.organisasjonsnr,
    bedriftsnr: props.enhet.firma?.bedriftsnr,
    enhetNavn: props.enhet.enhetnavn,
    gateadresse: props.enhet.besoksadresse?.gateadresse,
    postnummer: props.enhet.besoksadresse?.postnr,
    poststed: props.enhet.besoksadresse?.poststed,
    oppstartsDato: props.enhet.oppstartsdato,
  };

  const isGooglePlacesLinked = kildeDetaljer.some((k) => k.kilde === 'google-places');
  const showGooglePlacesSuggestion =
    !isGooglePlacesLinked &&
    (!props.enhet.hjemmeside || !props.enhet.firma?.telefon) &&
    !!props.enhet.enhetnavn &&
    !!(props.enhet.besoksadresse?.gateadresse || props.enhet.besoksadresse?.poststed);
  const googlePlacesLookupContext: EnhetLookupContext = {
    enhetNavn: props.enhet.enhetnavn,
    gateadresse: props.enhet.besoksadresse?.gateadresse,
    postnummer: props.enhet.besoksadresse?.postnr,
    poststed: props.enhet.besoksadresse?.poststed,
    latitude: props.enhet.besoksadresse?.latitude,
    longitude: props.enhet.besoksadresse?.longitude,
  };

  // Facebook only ever has something to find once a hjemmeside is known - scrapes that page for
  // an outbound link to a Facebook page rather than searching by name/address like the others.
  const isFacebookLinked = kildeDetaljer.some((k) => k.kilde === 'facebook');
  const showFacebookSuggestion = !isFacebookLinked && !!props.enhet.hjemmeside;
  const facebookLookupContext: EnhetLookupContext = {
    hjemmeside: props.enhet.hjemmeside,
  };

  const anyKildeSuggestion =
    brregHasSuggestion || googlePlacesHasSuggestion || facebookHasSuggestion;

  return (
    <>
      {formData.values ? (
        <Form onSubmit={formData.handleSubmit}>
          <div className="mb-3 d-flex align-items-center flex-wrap gap-2">
            {anyKildeSuggestion && <span className="text-muted small">Kildeforslag:</span>}
            {showBrregSuggestion && (
              <KildeSuggestionInline
                stepName="brreg-underenhet"
                context={brregLookupContext}
                enhetId={props.enhet.id}
                currentUser={currentUser}
                onLinked={props.onUpdated}
                onResult={setBrregHasSuggestion}
              />
            )}
            {showGooglePlacesSuggestion && (
              <KildeSuggestionInline
                stepName="google-places"
                context={googlePlacesLookupContext}
                enhetId={props.enhet.id}
                currentUser={currentUser}
                onLinked={props.onUpdated}
                onResult={setGooglePlacesHasSuggestion}
              />
            )}
            {showFacebookSuggestion && (
              <KildeSuggestionInline
                stepName="facebook"
                context={facebookLookupContext}
                enhetId={props.enhet.id}
                currentUser={currentUser}
                onLinked={props.onUpdated}
                onResult={setFacebookHasSuggestion}
              />
            )}
          </div>
          <Row>
            <Form.Group className={Style.formGroup} as={Col} sm={6}>
              <Form.Label>Enhetnavn</Form.Label>
              <Form.Control
                id="enhetnavn"
                type="text"
                name="text"
                value={formData.values.enhetnavn || ''}
                onChange={(e) => handleInputChange('enhetnavn', e.target.value)}
                isInvalid={formData.touched.enhetnavn && formData.errors.enhetnavn}
                isValid={formData.touched.enhetnavn && !formData.errors.enhetnavn}
              />
              <Form.Control.Feedback type="invalid">
                {formData.errors.enhetnavn}
              </Form.Control.Feedback>
              {!isEnhetnavnFormatCorrect && (
                <div className="text-primary">Avdeling skal forkortes AVD.xxxx</div>
              )}
            </Form.Group>

            <Form.Group className={Style.formGroup} as={Col} sm={6}>
              <Form.Label>
                Telefon
                <FieldSourcesPopover
                  field="telefon"
                  kildeDetaljer={kildeDetaljer}
                  currentValue={formData.values.telefon}
                  normalize={(v) => (v || '').replace(/\D/g, '')}
                  onUseValue={(v) => handleInputChangeClean('telefon', v)}
                />
              </Form.Label>
              <Form.Control
                id="telefon"
                name="telefon"
                type="text"
                value={formData.values.telefon || ''}
                onChange={(e) => handleInputChangeClean('telefon', e.target.value)}
                isInvalid={formData.touched.telefon && formData.errors.telefon}
                isValid={formData.touched.telefon && !formData.errors.telefon}
              />
              <Form.Control.Feedback type="invalid">
                {formData.errors.telefon}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row>
            <Form.Group className={Style.formGroup} as={Col} sm={6}>
              <Form.Label>
                Organisasjonsnummer
                {isOrgnummerInElma !== undefined &&
                  (isOrgnummerInElma ? (
                    <span
                      className="badge rounded-pill bg-success ms-1"
                      style={{ fontSize: '0.7em', verticalAlign: 'middle' }}
                      title="Registrert i Peppol-nettverket for e-faktura"
                    >
                      Peppol
                    </span>
                  ) : (
                    <span
                      className="badge rounded-pill bg-warning ms-1"
                      style={{ fontSize: '0.7em', verticalAlign: 'middle' }}
                      title="Ikke funnet i Peppol-nettverket - ikke en feil, bare betyr at søket ikke fant treff"
                    >
                      Ikke i Peppol
                    </span>
                  ))}
              </Form.Label>
              <OrgnrInput
                type="orgnr"
                selected={selectedOrgnummer}
                onChange={handleOrgnrChanged}
                onDagligLederChanged={setBrregDagligLeder}
                onNaeringskodeChanged={setNaeringskodeOrgnr}
              />
            </Form.Group>

            <Form.Group className={Style.formGroup} as={Col} sm={6}>
              <Form.Label>
                Epost
                <FieldSourcesPopover
                  field="epost"
                  kildeDetaljer={kildeDetaljer}
                  currentValue={formData.values.epost}
                  onUseValue={(v) => handleInputChange('epost', v)}
                />
              </Form.Label>
              <Form.Control
                id="epost"
                type="text"
                name="epost"
                value={formData.values.epost || ''}
                onChange={(e) => handleInputChange('epost', e.target.value.trim())}
                onBlur={(e) => verifyEmail(formData.values.epost)}
                isInvalid={formData.touched.epost && formData.errors.epost}
              />
              <Form.Control.Feedback type="invalid">{formData.errors.epost}</Form.Control.Feedback>
              {!isEmailValid && (
                <div className="warning-feedback">
                  <span>
                    Det finnes ingen aktiv e-post konto med denne adressen. Har det sneket seg inn
                    en skrivefeil? Kanskje noen har sluttet i jobben?
                  </span>
                </div>
              )}
            </Form.Group>
          </Row>

          <Row>
            <Form.Group id="juridiskbedriftinfo" className={Style.formGroup} as={Col} sm={6}>
              <Form.Label>
                Bedriftsnummer
                {isBedriftsnummerInElma !== undefined &&
                  (isBedriftsnummerInElma ? (
                    <span
                      className="badge rounded-pill bg-success ms-1"
                      style={{ fontSize: '0.7em', verticalAlign: 'middle' }}
                      title="Registrert i Peppol-nettverket for e-faktura"
                    >
                      Peppol
                    </span>
                  ) : (
                    <span
                      className="badge rounded-pill bg-warning ms-1"
                      style={{ fontSize: '0.7em', verticalAlign: 'middle' }}
                      title="Ikke funnet i Peppol-nettverket - ikke en feil, bare betyr at søket ikke fant treff"
                    >
                      Ikke i Peppol
                    </span>
                  ))}
              </Form.Label>
              <OrgnrInput
                type="bedriftsnr"
                selected={selectedBedriftsnummer}
                onChange={handleBedriftsnummerChanged}
                onNaeringskodeChanged={setNaeringskodeBedrift}
              />
            </Form.Group>

            <Form.Group className={Style.formGroup} as={Col} sm={6}>
              <Form.Label>Faktura Epost</Form.Label>
              <Form.Control
                id="fakturaepost"
                type="text"
                name="fakturaepost"
                value={formData.values.fakturaepost || ''}
                onChange={(e) => handleInputChange('fakturaepost', e.target.value.trim())}
                onBlur={(e) => verifyFaktura(e.target.value)}
                isInvalid={formData.touched.fakturaepost && formData.errors.fakturaepost}
              />
              <Form.Control.Feedback type="invalid">
                {formData.errors.fakturaepost}
              </Form.Control.Feedback>

              {props.kontonrOgFakturaAdresseSjekkResponse &&
                props.kontonrOgFakturaAdresseSjekkResponse.harSammeOrgNr &&
                props.kontonrOgFakturaAdresseSjekkResponse.hovedKontorFakturaEpost?.length > 0 &&
                props.kontonrOgFakturaAdresseSjekkResponse.fakturaEpostErForskjellig && (
                  <Form.Text>
                    Faktura Epost til{' '}
                    <a
                      target="_blank"
                      href={
                        '/enheter/' + props.kontonrOgFakturaAdresseSjekkResponse.hovedkontorEnhetId
                      }
                    >
                      hovedkontoret
                    </a>{' '}
                    er {props.kontonrOgFakturaAdresseSjekkResponse.hovedKontorFakturaEpost}.
                    <span> </span>
                    <a
                      target="_blank"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleInputChangeClean(
                          'fakturaepost',
                          props.kontonrOgFakturaAdresseSjekkResponse.hovedKontorFakturaEpost,
                        );
                      }}
                    >
                      Bytte?
                    </a>
                  </Form.Text>
                )}
              {!isFakturaEpostValid && (
                <div className="warning-feedback">
                  <span>
                    Det finnes ingen aktiv e-post konto med denne adressen. Har det sneket seg inn
                    en skrivefeil? Kanskje noen har sluttet i jobben?
                  </span>
                </div>
              )}
            </Form.Group>
          </Row>

          <Row>
            <Form.Group className={Style.formGroup} as={Col} sm={6}>
              <Form.Label>
                Oppstart dato
                <FieldSourcesPopover
                  field="oppstartsdato"
                  kildeDetaljer={kildeDetaljer}
                  currentValue={
                    formData.values.oppstartsdato
                      ? format(formData.values.oppstartsdato, 'yyyy-MM-dd')
                      : undefined
                  }
                  onUseValue={(v) => handleOppstartDatoChange(new Date(v))}
                  formatValue={(v) => {
                    const [year, month, day] = v.split('-');
                    return year && month && day ? `${day}.${month}.${year}` : v;
                  }}
                />
              </Form.Label>
              <div>
                <NorwegianDatepicker
                  id="oppstartsdato"
                  value={formData.values.oppstartsdato}
                  onChange={(value) => handleOppstartDatoChange(value)}
                  loading={loadingFørsteMuligeOppstartDato}
                  minDate={førsteMuligeOppstartDato}
                  showFirstDaySuggestions
                />
                <br />
                {formData.values.oppstartsdato &&
                  formData.touched.oppstartsdato &&
                  !formData.values.reservertMotPost && (
                    <Form.Text className="text-primary">
                      Enheten får post fra: <strong>{postFrom}</strong>
                    </Form.Text>
                  )}

                {formData.touched.oppstartsdato &&
                  (props.enhet.sisteRegionalkjedeMedlemskap?.isActive ||
                    props.enhet.sisteMarkedskjedeMedlemskap?.isActive ||
                    props.enhet.sisteGrupperingMedlemskap?.isActive) && (
                    <div className="text-warning mt-1" style={{ fontSize: '0.875em' }}>
                      Husk å sjekke om medlemskapsdatoene for kjede- og/eller enhetsgruppering må
                      justeres.
                    </div>
                  )}

                <Form.Control.Feedback type="invalid">
                  {formData.errors.oppstartsdato}
                </Form.Control.Feedback>
              </div>
            </Form.Group>

            <Form.Group className={Style.formGroup} as={Col}>
              <Form.Label>Reservert mot post</Form.Label>
              <Form.Check
                id="cb-1"
                type="checkbox"
                disabled={formData.values.oppstartsdato ? false : true}
                checked={formData.values.reservertMotPost}
                onChange={(e) => formData.handleChange('reservertMotPost', e.target.checked)}
              />
              <Form.Control.Feedback type="invalid">
                {formData.errors.reservertMotPost}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className={Style.formGroup} as={Col}>
              <Form.Label>Sesongdrift</Form.Label>
              <Form.Check
                type="checkbox"
                checked={formData.values.erSesongDrift}
                onChange={(e) => formData.handleChange('erSesongDrift', e.target.checked)}
              />
              <Form.Control.Feedback type="invalid">
                {formData.errors.erSesongdrift}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className={Style.formGroup} as={Col}>
              <Form.Label>Offentlig</Form.Label>
              <Form.Check
                type="checkbox"
                checked={formData.values.erOffentlig}
                onChange={(e) => formData.handleChange('erOffentlig', e.target.checked)}
              />
              <Form.Control.Feedback type="invalid">
                {formData.errors.erOffentlig}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className={Style.formGroup} as={Col}>
              <Form.Label>Kan motta efaktura</Form.Label>
              <Form.Check
                type="checkbox"
                disabled={true}
                checked={formData.values.kanMottaEfaktura}
              />
              <Form.Control.Feedback type="invalid">
                {formData.errors.kanMottaEfaktura}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row>
            <Form.Group className={Style.formGroup} as={Col} sm={6}>
              <Form.Label>Omsetning</Form.Label>
              <Form.Control
                id="omsetning"
                type="number"
                step="any"
                name="omsetningMillioner"
                value={formData.values.omsetningMillioner || ''}
                onChange={(e) => formData.handleChange('omsetningMillioner', e.target.value)}
                isInvalid={
                  formData.touched.omsetningMillioner && formData.errors.omsetningMillioner
                }
                isValid={formData.touched.omsetningMillioner && !formData.errors.omsetningMillioner}
              />
              <Form.Text>I millioner</Form.Text>
              <Form.Control.Feedback type="invalid">
                {formData.errors.omsetningMillioner}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className={Style.formGroup} as={Col} sm={6}>
              <Form.Label>Omsetningsårstall</Form.Label>
              <Form.Control
                id="omsetningårstall"
                type="number"
                step="1"
                name="omsetningAar"
                value={formData.values.omsetningAar || ''}
                onChange={(e) => formData.handleChange('omsetningAar', e.target.value)}
                isInvalid={formData.touched.omsetningAar && formData.errors.omsetningAar}
                isValid={formData.touched.omsetningAar && !formData.errors.omsetningAar}
              />
              <Form.Text>kan ikke endres for eksisterende omsetninger</Form.Text>
              <Form.Control.Feedback type="invalid">
                {formData.errors.omsetningAar}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row>
            <Form.Group className={Style.formGroup} as={Col} sm={6}>
              <Form.Label>Kontonummer</Form.Label>
              <Form.Control
                id="kontonummer"
                type="text"
                name="kontonummer"
                value={formData.values.kontonr || ''}
                onChange={(e) => handleInputChangeClean('kontonr', e.target.value)}
                isInvalid={formData.touched.kontonr && formData.errors.kontonr}
                isValid={formData.touched.kontonr && !formData.errors.kontonr}
              />
              <Form.Control.Feedback type="invalid">
                {formData.errors.kontonr}
              </Form.Control.Feedback>
              {props.kontonrOgFakturaAdresseSjekkResponse &&
                props.kontonrOgFakturaAdresseSjekkResponse.hovedkontorKontonr &&
                props.kontonrOgFakturaAdresseSjekkResponse.harSammeOrgNr &&
                props.kontonrOgFakturaAdresseSjekkResponse.kontonrErForskjellig && (
                  <Form.Text>
                    Kontonummer til{' '}
                    <a
                      target="_blank"
                      href={
                        '/enheter/' + props.kontonrOgFakturaAdresseSjekkResponse.hovedkontorEnhetId
                      }
                    >
                      hovedkontoret
                    </a>{' '}
                    er {props.kontonrOgFakturaAdresseSjekkResponse.hovedkontorKontonr}.
                    <span> </span>
                    <a
                      target="_blank"
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        handleInputChangeClean(
                          'kontonr',
                          props.kontonrOgFakturaAdresseSjekkResponse.hovedkontorKontonr,
                        );
                      }}
                    >
                      Bytte?
                    </a>
                  </Form.Text>
                )}
            </Form.Group>

            <Form.Group className={Style.formGroup} as={Col} sm={6}>
              <Form.Label>Salgsareal</Form.Label>
              <Form.Control
                id="salgsareal"
                type="number"
                name="salgsArealKvadratmeter"
                value={formData.values.salgsArealKvadratmeter || ''}
                onChange={(e) => formData.handleChange('salgsArealKvadratmeter', e.target.value)}
                isInvalid={
                  formData.touched.salgsArealKvadratmeter && formData.errors.salgsArealKvadratmeter
                }
                isValid={
                  formData.touched.salgsArealKvadratmeter && !formData.errors.salgsArealKvadratmeter
                }
              />
              <Form.Text>I kvadratmeter</Form.Text>
              <Form.Control.Feedback type="invalid">
                {formData.errors.salgsArealKvadratmeter}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          {/*
            Dagligleder og Hjemmeside rad
          */}
          <Row>
            <Form.Group className={Style.formGroup} as={Col} sm={6}>
              <Form.Label>
                Daglig leder
                {/* Daglig leder is always tied to organisasjonsnummer (juridisk hovedenhet) -
                    if this Enhet has its own bedriftsnr (underenhet), that hovedenhet-level
                    person usually isn't who actually runs this specific location. */}
                {brregDagligLeder && !selectedBedriftsnummer && (
                  <span
                    role="button"
                    className={`badge rounded-pill ms-1 ${
                      (formData.values.dagligLeder || '').trim().toLowerCase() ===
                      brregDagligLeder.trim().toLowerCase()
                        ? 'bg-success'
                        : 'bg-warning'
                    }`}
                    style={{ cursor: 'pointer', fontSize: '0.7em', verticalAlign: 'middle' }}
                    onClick={() => formData.handleChange('dagligLeder', brregDagligLeder)}
                    title={`Brreg: ${brregDagligLeder}`}
                  >
                    Brreg
                  </span>
                )}
              </Form.Label>
              <Form.Control
                id="dagligLeder"
                type="text"
                name="dagligLeder"
                value={formData.values.dagligLeder || ''}
                onChange={(e) => formData.handleChange('dagligLeder', e.target.value)}
                isInvalid={formData.touched.dagligLeder && formData.errors.dagligLeder}
                isValid={formData.touched.dagligLeder && !formData.errors.dagligLeder}
              />
              <Form.Control.Feedback type="invalid">
                {formData.errors.dagligLeder}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className={Style.formGroup} as={Col} sm={6}>
              <Form.Label>
                Hjemmeside
                <FieldSourcesPopover
                  field="hjemmeside"
                  kildeDetaljer={kildeDetaljer}
                  currentValue={formData.values.hjemmeside}
                  normalize={(v) =>
                    (v || '')
                      .toLowerCase()
                      .replace(/^https?:\/\//, '')
                      .replace(/^www\./, '')
                      .replace(/\/$/, '')
                  }
                  onUseValue={(v) => {
                    formData.handleChange('hjemmeside', v);
                    updateHjemmesideLink(v);
                  }}
                />
              </Form.Label>
              <Form.Control
                id="hjemmeside"
                type="text"
                name="hjemmeside"
                value={formData.values.hjemmeside || ''}
                onChange={(e) => formData.handleChange('hjemmeside', e.target.value)}
                onBlur={() => updateHjemmesideLink(formData.values.hjemmeside)}
                isInvalid={formData.touched.hjemmeside && formData.errors.hjemmeside}
                isValid={formData.touched.hjemmeside && !formData.errors.hjemmeside}
              />
              <Form.Control.Feedback type="invalid">
                {formData.errors.hjemmeside}
              </Form.Control.Feedback>
              {hjemmesideLink && (
                <Form.Text>
                  <a href={hjemmesideLink} target="_blank" rel="noopener noreferrer">
                    {hjemmesideLink}
                  </a>
                </Form.Text>
              )}
            </Form.Group>
          </Row>

          <Row>
            <Form.Group
              className={Style.formGroup}
              id="bransje"
              as={Col}
              lg={segmentErRelevant ? 4 : 6}
            >
              <Form.Label>
                Bransje
                {brregNaeringskode && (
                  <span
                    className="badge rounded-pill bg-secondary ms-1"
                    style={{ fontSize: '0.7em', verticalAlign: 'middle' }}
                    title={`Brreg NACE-kode${naeringskodeBedrift ? ' (underenhet)' : ' (juridisk enhet)'}: ${brregNaeringskode.kode} ${brregNaeringskode.beskrivelse}`}
                  >
                    {brregNaeringskode.kode} {brregNaeringskode.beskrivelse}
                  </span>
                )}
              </Form.Label>
              <BransjePicker
                onSelectionChanged={handlePickedBransjeChanged}
                selected={[pickedBransjeID]}
                mode="radioSelect"
              />
            </Form.Group>
            {segmentErRelevant && (
              <Form.Group className={Style.formGroup} id="segment" as={Col} lg={4}>
                <Form.Label>Segment</Form.Label>
                <SegmentPicker
                  bransjeId={pickedBransjeID}
                  enhetId={props.enhet.id}
                  selected={formData.values.segment}
                  onChange={(segment) => formData.handleChange('segment', segment ?? null)}
                />
              </Form.Group>
            )}
            <Form.Group className={Style.formGroup} as={Col} lg={segmentErRelevant ? 4 : 6}>
              <Form.Label>Land</Form.Label>
              <CountryPicker
                selected={formData.values.landKode || 'NO'}
                onChange={(value) => formData.handleChange('landKode', value)}
              />
              <Form.Control.Feedback type="invalid">
                {formData.errors.landKode}
              </Form.Control.Feedback>
            </Form.Group>
          </Row>

          <Row>
            <Form.Group className={Style.formGroup} as={Col} lg={6}>
              <Form.Label>Enhettyper</Form.Label>
              <EnhetTypeMultiPicker
                resetIndicator={pickerResetIndicator}
                treeSelectMode="multiSelect"
                onSelectionChanged={handleSelectedTyperChanged}
                selectedNodes={formData.values.enhetTypeKoder}
                disabled={isSaveLoading}
                closeOnSelect={true}
              />
            </Form.Group>
            <Form.Group className={Style.formGroup} as={Col} lg={6}>
              <Form.Label>Hovedtype</Form.Label>
              <Form.Control
                id="enhettyper"
                disabled={formData.values.enhetTypeKoder?.length < 1}
                as="select"
                onChange={(e) => formData.handleChange('hovedEnhetTypeKode', e.target.value)}
                value={formData.values.hovedEnhetTypeKode || ''}
                //custom
              >
                {formData.values.enhetTypeKoder &&
                  formData.values.enhetTypeKoder.map((kode) => (
                    <option key={kode} value={kode}>
                      {kode}
                    </option>
                  ))}
              </Form.Control>
            </Form.Group>
          </Row>

          <Row>
            <Form.Group className={Style.formGroup} as={Col} sm={12}>
              <Form.Label>Notat</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="notat"
                value={formData.values.notat || ''}
                onChange={(e) => formData.handleChange('notat', e.target.value)}
                isInvalid={formData.touched.notat && formData.errors.notat}
                isValid={formData.touched.notat && !formData.errors.notat}
              />
              <Form.Control.Feedback type="invalid">{formData.errors.notat}</Form.Control.Feedback>
              <Button
                variant="outline-secondary"
                size="sm"
                className="mt-2"
                onClick={insertNameAndDate}
              >
                Sett inn navn og dato
              </Button>
            </Form.Group>
          </Row>
          <Row style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Form.Group className={Style.formGroup}>
              <IcebergButton
                variant="primary"
                icon="disk"
                type="submit"
                disabled={formData.submitDisabled}
                isLoading={isSaveLoading}
              >
                <span>Lagre</span>
              </IcebergButton>
            </Form.Group>
          </Row>
        </Form>
      ) : (
        <div>Laster</div>
      )}
    </>
  );
});

export default Basis;
